import { DeviceWithVehicleInfo } from "@/models/DeviceWithVehicleInfo";
import { LiveDataRow } from "@/pages/live-data/models/LiveDataRow";
import { canProtocolNames } from "@/pages/live-data/utilities/checklistGroups";
import { odometerValue } from "@/pages/live-data/utilities/checklistValues";
import {
  CheckKey,
  isFuelLevelPercentRow,
  isFuelLevelVolumeRow,
  isOdometerRow,
  isRawOdometerRow,
  matchesCheck,
} from "@/pages/live-data/utilities/diagnosticMatchers";
import { ioxDeviceNames } from "@/pages/live-data/utilities/ioxDevices";
import { getFirmwareValue } from "@/utilities/getFirmwareValue";

/**
 * How a vehicle came out of the check overall.
 *
 * "Not scanned" is a state of its own. Without it a vehicle nobody has looked at yet is
 * indistinguishable from one that was looked at and reported nothing — so opening the page showed
 * the entire fleet as failed, and a search that turned up a vehicle outside the scan showed it as
 * failed too.
 */
export type CheckStatus = "notScanned" | "ok" | "warn" | "alert";

/** Which kind of wiring harness the vehicle reported, if any. */
export type HarnessKind = "intelligent" | "standard";

/**
 * The values shown as one tick per column.
 *
 * A list rather than separate fields, so the columns can be generated from it — the original had
 * eleven near-identical column definitions and eleven near-identical lookups beside them.
 */
export const REPORTED_CHECKS = [
  "ignition",
  "vehicleSpeed",
  "engineSpeed",
  "deviceVoltage",
  "fuelLevel",
  "fuelLevelLiters",
  "defLevel",
  "hvBatteryVoltage",
  "hvBatteryCurrent",
  "hvBatteryPower",
  "stateOfCharge",
] as const;

export type ReportedCheck = (typeof REPORTED_CHECKS)[number];

/**
 * How each of those values is recognised.
 *
 * Asked of the matchers directly. The original derived these from the single-vehicle checklist by
 * looking its items up under their *translated* labels — so renaming a German label, or running in
 * a language whose translation had drifted, silently turned every check into "not reported".
 */
/**
 * The two checks that are not simply a rule of their own name.
 *
 * Both read the same diagnostic and are told apart by its unit, so they cannot be addressed by a
 * key. Listing only the exceptions keeps that fact visible instead of burying it in a table where
 * every other entry repeats its own key.
 */
const UNIT_SPLIT: Partial<Record<ReportedCheck, (row: LiveDataRow) => boolean>> = {
  fuelLevel: isFuelLevelPercentRow,
  fuelLevelLiters: isFuelLevelVolumeRow,
};

function predicateFor(check: ReportedCheck): (row: LiveDataRow) => boolean {
  return UNIT_SPLIT[check] ?? (row => matchesCheck(check as CheckKey, row));
}

/**
 * How far the two odometers may differ before it is worth pointing out, in whatever unit they
 * are shown in. Below that they disagree only by rounding.
 */
const ODOMETER_TOLERANCE = 1;

/** One vehicle's line in the fleet-wide check. */
export interface FleetCheckRow {
  id: string;
  device: DeviceWithVehicleInfo;
  /** The readings the line was built from, for the expandable checklist underneath it. */
  rows: LiveDataRow[];
  /** No scan has covered this vehicle yet. */
  name: string;
  licensePlate: string;
  serial: string;
  vin: string;
  /** Make and model as decoded from the VIN, where that lookup answered. */
  vinDecoded: string;
  /** Whether the telematics unit is currently communicating; `undefined` while not yet known. */
  isCommunicating: boolean | undefined;
  /** The newest reading of any kind, which says how current everything else on the line is. */
  lastRecordAt: Date | null;
  rawOdometer: number | null;
  /** Each carries its own unit: the measurement profile can cover one of the two and not the other. */
  rawOdometerUnit: string;
  odometer: number | null;
  odometerUnit: string;
  /** The two odometers disagree by more than rounding, in the same unit. */
  odometerMismatch: boolean;
  reported: Record<ReportedCheck, boolean>;
  firmware: string;
  harness: HarnessKind | null;
  fmsStandardSoftwareVersion: string;
  canProtocols: string[];
  ioxDevices: string[];
  status: CheckStatus;
}

export interface BuildFleetCheckRowInput {
  device: DeviceWithVehicleInfo;
  /**
   * The newest reading per diagnostic the vehicle produced inside the scanned window, or `null`
   * where no scan has covered it.
   *
   * One input rather than a list plus a "was it scanned" flag: with two, "not scanned but here
   * are its readings" is a state the type allows and nothing means.
   */
  rows: LiveDataRow[] | null;
  /** Whether the unit is communicating, from the fleet status query. */
  isCommunicating?: boolean;
  /** Make and model decoded from the VIN, where known. */
  vinDecoded?: string;
}

/**
 * Turns one vehicle's readings into its line of the fleet-wide check.
 *
 * The same questions the single-vehicle checklist asks, answered from the same rules — but as one
 * row of ticks rather than a page, and without the 24-hour fallback: the scan already covers a
 * window the caller chose, so "reported inside the window" is the whole question.
 */
export function buildFleetCheckRow(input: BuildFleetCheckRowInput): FleetCheckRow {
  const { device, rows, isCommunicating, vinDecoded = "" } = input;
  const readings = rows ?? [];

  const reported = {} as Record<ReportedCheck, boolean>;
  for (const check of REPORTED_CHECKS) {
    reported[check] = readings.some(predicateFor(check));
  }

  const rawOdometer = odometerValue(readings.find(isRawOdometerRow));
  const odometer = odometerValue(readings.find(isOdometerRow));
  const canProtocols = canProtocolNames(readings);
  const firmware = getFirmwareValue(device);
  const harness = harnessKind(readings);

  // What has to be there before a vehicle can be signed off at all. Everything else on the line
  // depends on what the vehicle is — a diesel reports no state of charge, and that is not a fault.
  const core = [reported.ignition, reported.vehicleSpeed, firmware !== "", harness !== null, canProtocols.length > 0];
  const met = core.filter(Boolean).length;

  return {
    id: device.id,
    device,
    rows: readings,
    name: device.name ?? "",
    licensePlate: device.licensePlate ?? "",
    serial: device.serialNumber ?? "",
    vin: device.vehicleIdentificationNumber ?? "",
    vinDecoded,
    isCommunicating,
    lastRecordAt: newestReading(readings),
    rawOdometer: rawOdometer?.value ?? null,
    rawOdometerUnit: rawOdometer?.unit ?? "",
    odometer: odometer?.value ?? null,
    odometerUnit: odometer?.unit ?? "",
    // Only comparable in the same unit. The measurement profile can cover one of the two
    // diagnostics and not the other, and then the difference is the unit, not the mileage.
    odometerMismatch:
      rawOdometer !== null &&
      odometer !== null &&
      rawOdometer.unit === odometer.unit &&
      Math.abs(rawOdometer.value - odometer.value) >= ODOMETER_TOLERANCE,
    reported,
    firmware,
    harness,
    fmsStandardSoftwareVersion: reportedValue(readings, "fmsStandardSoftwareVersion"),
    canProtocols,
    ioxDevices: ioxDeviceNames(readings),
    status: rows === null ? "notScanned" : met === core.length ? "ok" : met === 0 ? "alert" : "warn",
  };
}

/**
 * Which harness the vehicle reported.
 *
 * The intelligent one wins where both match: it is the more specific answer, and a database that
 * names its reading so that both patterns fit is describing an intelligent harness.
 */
function harnessKind(rows: LiveDataRow[]): HarnessKind | null {
  if (rows.some(row => matchesCheck("intelligentHarness", row))) {
    return "intelligent";
  }
  return rows.some(row => matchesCheck("standardHarness", row)) ? "standard" : null;
}

function newestReading(rows: LiveDataRow[]): Date | null {
  let newest: Date | null = null;

  for (const row of rows) {
    if (newest === null || row.dateTime > newest) {
      newest = row.dateTime;
    }
  }

  return newest;
}

/**
 * A value shown as text rather than as a tick.
 *
 * Never run through a number formatter: a software version is an identifier, and "3.1" turned
 * into "3,1" by a German locale is a different string, not a translated one.
 */
function reportedValue(rows: LiveDataRow[], check: "fmsStandardSoftwareVersion"): string {
  const row = rows.find(candidate => matchesCheck(check, candidate));
  return row ? String(row.value) : "";
}
